import { forwardRef, type ReactNode } from 'react'

const styles = {
  container: {
    position: 'relative',
    width: '100%',
    minHeight: '20px'
  },
  overlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1000,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    transition: 'all 0.3s'
  },
  content: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  tip: {
    marginTop: '8px',
    color: '#1976d2',
    fontSize: '14px',
    textShadow: '0 1px 2px #fff'
  },
  progress: {
    animation: 'spin-rotate 1.4s linear infinite',
    display: 'block'
  },
  progressCircle: {
    strokeDasharray: '80px, 200px',
    strokeDashoffset: 0,
    strokeLinecap: 'round',
    animation: 'spin-dash 1.4s ease-in-out infinite'
  }
} as const

// 定义关键帧动画
const rotateKeyframes = `
  @keyframes spin-rotate {
    100% {
      transform: rotate(360deg);
    }
  }
  @keyframes spin-dash {
    0% {
      stroke-dasharray: 1px, 200px;
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: 100px, 200px;
      stroke-dashoffset: -15px;
    }
    100% {
      stroke-dasharray: 100px, 200px;
      stroke-dashoffset: -125px;
    }
  }
`

// 添加关键帧动画到 document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style')
  styleSheet.textContent = rotateKeyframes
  document.head.appendChild(styleSheet)
}

export interface SpinProps {
  loading?: boolean
  indicator?: ReactNode
  tip?: string
  size?: number | 'small' | 'default' | 'large'
  color?: string
  thickness?: number
  wrapperClassName?: string
  children?: ReactNode
  wrapperStyle?: React.CSSProperties
}

const sizeMap = {
  small: 24,
  default: 40,
  large: 56
}

const DefaultSpinner = ({
  size = 40,
  thickness = 3.6,
  color = '#1976d2'
}: {
  size?: number | keyof typeof sizeMap
  thickness?: number
  color?: string
}) => {
  const finalSize = typeof size === 'number' ? size : sizeMap[size]

  return (
    <div
      role='progressbar'
      aria-busy={true}
      aria-valuemin={0}
      aria-valuemax={100}>
      <svg
        style={{
          ...styles.progress,
          width: finalSize,
          height: finalSize
        }}
        viewBox='0 0 44 44'>
        <circle
          style={styles.progressCircle}
          cx='22'
          cy='22'
          r='20'
          fill='none'
          strokeWidth={thickness}
          stroke={color}
        />
      </svg>
    </div>
  )
}

const Spin = forwardRef<HTMLDivElement, SpinProps>(
  (
    {
      loading = false,
      indicator,
      tip,
      size = 'default',
      color,
      thickness = 3.6,
      wrapperClassName = '',
      children,
      wrapperStyle = {}
    },
    ref
  ) => {
    const spinIndicator = indicator || (
      <DefaultSpinner size={size} thickness={thickness} color={color} />
    )

    return (
      <div
        ref={ref}
        style={{ ...styles.container, ...wrapperStyle }}
        className={wrapperClassName}>
        {children}
        {loading && (
          <div style={styles.overlay}>
            <div style={styles.content}>
              {spinIndicator}
              {tip && <div style={styles.tip}>{tip}</div>}
            </div>
          </div>
        )}
      </div>
    )
  }
)

Spin.displayName = 'Spin'

export default Spin
